import React from "react";
import PluginPlaceholder from "../../_editor/components/canvas/plugin_placeholder/PluginPlaceholder";
import "./_truefalse.scss";
import i18n from "i18next";
import { contentT } from "../../common/content_language";
import { letterFromNumber, setRgbaAlpha, getButtonText } from "../../common/common_tools";
import AnswerButton from "../FreeResponse/visor/SubmitAnswerButton";
import {
  getTrueFalseOptionVars,
  getTrueFalseOptionDisplayColor,
  getOptionStateKey,
  TRUE_FALSE_OPTION_COLOR_KEYS,
} from "./trueFalseButtonStyle";
/* eslint-disable react/prop-types */

// Labels of the per-option colour pickers (shared with "Button Style").
const OPTION_COLOR_LABELS = {
  buttonBackgroundColor: "TrueFalse.ButtonBackground",
  buttonTextColor: "TrueFalse.ButtonTextColor",
  buttonBorderColor: "TrueFalse.ButtonBorderColor",
  buttonHoverBackgroundColor: "TrueFalse.ButtonHoverBackground",
};

// "Doğru Düğmesi" / "Yanlış Düğmesi" accordion: colours of one option button.
// Unset colours inherit "Button Style", so existing boxes look unchanged.
function optionColorAccordion(state, kind) {
  const buttons = {};
  TRUE_FALSE_OPTION_COLOR_KEYS.forEach((key) => {
    buttons[getOptionStateKey(kind, key)] = {
      __name: IdealStudio.i18n.t(OPTION_COLOR_LABELS[key]),
      type: "color",
      value: getTrueFalseOptionDisplayColor(state, kind, key),
    };
  });
  return {
    __name: IdealStudio.i18n.t(
      kind === "true" ? "TrueFalse.TrueButtonStyle" : "TrueFalse.FalseButtonStyle"
    ),
    icon: "palette",
    buttons,
  };
}

export function TrueFalse(base) {
  return {
    getConfig: function () {
      return {
        name: "TrueFalse",
        displayName: IdealStudio.i18n.t("TrueFalse.PluginName"),
        category: "evaluation",
        icon: "check_circle",
        initialWidth: "100%",
        initialHeight: "100%",
        initialWidthSlide: "100%",
        initialHeightSlide: "100%",
        flavor: "react",
        isComplex: true,
        defaultCorrectAnswer: ["false", "false", "false"],
        defaultCurrentAnswer: ["", "", ""],
      };
    },
    getToolbar: function (state) {
      return {
        main: {
          __name: "Main",
          accordions: {
            __score: {
              __name: i18n.t("configuration"),
              icon: "build",
              buttons: {
                nBoxes: {
                  __name: i18n.t("TrueFalse.Number"),
                  type: "number",
                  value: state.nBoxes,
                  max: 6,
                  min: 1,
                },
                showFeedback: {
                  __name: i18n.t("MultipleChoice.ShowFeedback"),
                  type: "checkbox",
                  checked: state.showFeedback,
                },
                shuffle: {
                  __name: i18n.t("MultipleChoice.Shuffle"),
                  type: "checkbox",
                  checked: state.shuffle,
                },
                buttons: {
                  __name: i18n.t("MultipleChoice.WhichButtons"),
                  type: "radio",
                  value: state.buttons,
                  options: ["tryAgain", "seeAnswer"],
                  labels: [i18n.t("MultipleChoice.tryAgain"), i18n.t("MultipleChoice.seeAnswer")],
                },
                checkAnswerText: {
                  __name: i18n.t("TrueFalse.CheckAnswerText") || "Check Answer Button Text",
                  type: "text",
                  value: getButtonText(state.checkAnswerText, "MultipleChoice.checkAnswer"),
                },
                tryAgainText: {
                  __name: i18n.t("TrueFalse.TryAgainText") || "Try Again Button Text",
                  type: "text",
                  value: getButtonText(state.tryAgainText, "MultipleChoice.tryAgain"),
                  hide: state.buttons !== "tryAgain",
                },
                seeAnswerText: {
                  __name: i18n.t("TrueFalse.SeeAnswerText") || "See Answer Button Text",
                  type: "text",
                  value: getButtonText(state.seeAnswerText, "MultipleChoice.seeAnswer"),
                  hide: state.buttons !== "seeAnswer",
                },
                trueText: {
                  __name: i18n.t("TrueFalse.TrueText") || "True Button Text",
                  type: "text",
                  value: state.trueText || contentT("TrueFalse.True") || "True",
                },
                falseText: {
                  __name: i18n.t("TrueFalse.FalseText") || "False Button Text",
                  type: "text",
                  value: state.falseText || contentT("TrueFalse.False") || "False",
                },
                allowPartialScore: {
                  __name: i18n.t("MultipleAnswer.AllowPartialScore"),
                  type: "checkbox",
                  checked: state.allowPartialScore,
                },
                quizColor: {
                  __name: IdealStudio.i18n.t("TrueFalse.QuizColor"),
                  type: "color",
                  value: state.quizColor || "rgba(237, 233, 254, 1)",
                },
              },
            },
            __buttonStyle: {
              __name: IdealStudio.i18n.t("TrueFalse.ButtonStyle") || "Button Style",
              icon: "palette",
              buttons: {
                buttonBackgroundColor: {
                  __name:
                    IdealStudio.i18n.t("TrueFalse.ButtonBackground") || "Button Background Color",
                  type: "color",
                  value: state.buttonBackgroundColor || "#ede9fe",
                },
                buttonTextColor: {
                  __name: IdealStudio.i18n.t("TrueFalse.ButtonTextColor") || "Button Text Color",
                  type: "color",
                  value: state.buttonTextColor || "#111827",
                },
                buttonBorderColor: {
                  __name:
                    IdealStudio.i18n.t("TrueFalse.ButtonBorderColor") || "Button Border Color",
                  type: "color",
                  value: state.buttonBorderColor || "#ede9fe",
                },
                buttonBorderWidth: {
                  __name:
                    IdealStudio.i18n.t("TrueFalse.ButtonBorderWidth") || "Button Border Width",
                  type: "range",
                  value: state.buttonBorderWidth ?? 1,
                  min: 0,
                  max: 5,
                  step: 1,
                },
                buttonBorderRadius: {
                  __name:
                    IdealStudio.i18n.t("TrueFalse.ButtonBorderRadius") || "Button Border Radius",
                  type: "range",
                  value: state.buttonBorderRadius ?? 4,
                  min: 0,
                  max: 25,
                  step: 1,
                },
                buttonPadding: {
                  __name: IdealStudio.i18n.t("TrueFalse.ButtonPadding") || "Button Padding",
                  type: "range",
                  value: state.buttonPadding ?? 8,
                  min: 4,
                  max: 20,
                  step: 1,
                },
                buttonFontSize: {
                  __name: IdealStudio.i18n.t("TrueFalse.ButtonFontSize") || "Button Font Size",
                  type: "range",
                  value: state.buttonFontSize || 14,
                  min: 10,
                  max: 24,
                  step: 1,
                },
                buttonFontWeight: {
                  __name: IdealStudio.i18n.t("TrueFalse.ButtonFontWeight") || "Button Font Weight",
                  type: "select",
                  value: state.buttonFontWeight || "normal",
                  options: [
                    "normal",
                    "bold",
                    "100",
                    "200",
                    "300",
                    "400",
                    "500",
                    "600",
                    "700",
                    "800",
                    "900",
                  ],
                  labels: [
                    i18n.t("styles.font_weight.normal") || "normal",
                    i18n.t("styles.font_weight.bold") || "bold",
                    i18n.t("styles.font_weight.100") || "100",
                    i18n.t("styles.font_weight.200") || "200",
                    i18n.t("styles.font_weight.300") || "300",
                    i18n.t("styles.font_weight.400") || "400",
                    i18n.t("styles.font_weight.500") || "500",
                    i18n.t("styles.font_weight.600") || "600",
                    i18n.t("styles.font_weight.700") || "700",
                    i18n.t("styles.font_weight.800") || "800",
                    i18n.t("styles.font_weight.900") || "900",
                  ],
                },
                buttonHoverBackgroundColor: {
                  __name:
                    IdealStudio.i18n.t("TrueFalse.ButtonHoverBackground") ||
                    "Button Hover Background Color",
                  type: "color",
                  value: state.buttonHoverBackgroundColor || "#ede9fe",
                },
              },
            },
            // PluginToolbar sorts accordions by key: these names keep them right
            // below "Button Style" (__buttonStyle), True before False.
            __buttonStyle1True: optionColorAccordion(state, "true"),
            __buttonStyle2False: optionColorAccordion(state, "false"),
            style: {
              __name: IdealStudio.i18n.t("HotspotImages.box_style"),
              icon: "palette",
              buttons: {
                padding: {
                  __name: IdealStudio.i18n.t("HotspotImages.padding"),
                  type: "number",
                  value: 10,
                  min: 0,
                  max: 100,
                },
                backgroundColor: {
                  __name: IdealStudio.i18n.t("HotspotImages.background_color"),
                  type: "color",
                  value: "#ffffff",
                },
                borderWidth: {
                  __name: IdealStudio.i18n.t("HotspotImages.border_size"),
                  type: "number",
                  value: 1,
                  min: 0,
                  max: 10,
                },
                borderStyle: {
                  __name: IdealStudio.i18n.t("HotspotImages.border_style"),
                  type: "select",
                  value: "solid",
                  options: [
                    "none",
                    "hidden",
                    "dotted",
                    "dashed",
                    "solid",
                    "double",
                    "groove",
                    "ridge",
                    "inset",
                    "outset",
                    "initial",
                    "inherit",
                  ],
                  labels: [
                    i18n.t("styles.border_style_options.none") || "none",
                    i18n.t("styles.border_style_options.hidden") || "hidden",
                    i18n.t("styles.border_style_options.dotted") || "dotted",
                    i18n.t("styles.border_style_options.dashed") || "dashed",
                    i18n.t("styles.border_style_options.solid") || "solid",
                    i18n.t("styles.border_style_options.double") || "double",
                    i18n.t("styles.border_style_options.groove") || "groove",
                    i18n.t("styles.border_style_options.ridge") || "ridge",
                    i18n.t("styles.border_style_options.inset") || "inset",
                    i18n.t("styles.border_style_options.outset") || "outset",
                    i18n.t("styles.border_style_options.initial") || "initial",
                    i18n.t("styles.border_style_options.inherit") || "inherit",
                  ],
                },
                borderColor: {
                  __name: IdealStudio.i18n.t("HotspotImages.border_color"),
                  type: "color",
                  value: "#dbdbdb",
                },
                borderRadius: {
                  __name: IdealStudio.i18n.t("HotspotImages.radius"),
                  type: "number",
                  value: 0,
                  min: 0,
                  max: 50,
                },
                opacity: {
                  __name: IdealStudio.i18n.t("HotspotImages.opacity"),
                  type: "range",
                  value: 1,
                  min: 0,
                  max: 1,
                  step: 0.01,
                },
              },
            },
          },
        },
      };
    },
    getInitialState: function () {
      return {
        nBoxes: 3,
        showFeedback: true,
        shuffle: false,
        buttons: "tryAgain",
        allowPartialScore: true,
        quizColor: "rgba(237, 233, 254, 1)",
        checkAnswerText: null,
        tryAgainText: null,
        seeAnswerText: null,
        trueText: null,
        falseText: null,
        buttonBackgroundColor: "#ede9fe",
        buttonTextColor: "#111827",
        buttonBorderColor: "#ede9fe",
        buttonBorderWidth: 1,
        buttonBorderRadius: 4,
        buttonPadding: 8,
        buttonFontSize: 14,
        buttonFontWeight: "normal",
        buttonHoverBackgroundColor: "#ede9fe",
        // Per-option colours; null = inherit "Button Style".
        trueButtonBackgroundColor: null,
        trueButtonTextColor: null,
        trueButtonBorderColor: null,
        trueButtonHoverBackgroundColor: null,
        falseButtonBackgroundColor: null,
        falseButtonTextColor: null,
        falseButtonBorderColor: null,
        falseButtonHoverBackgroundColor: null,
      };
    },
    getRenderTemplate: function (state, props = {}) {
      let answers = [];

      let quizColor = state.quizColor || "rgba(237, 233, 254, 1)";
      for (let i = 0; i < state.nBoxes; i++) {
        let clickHandler = (index, value) => {
          if (
            props.exercises &&
            props.exercises.correctAnswer &&
            props.exercises.correctAnswer instanceof Array
          ) {
            let nBoxes = Array(state.nBoxes).fill("");
            let newAnswer = nBoxes.map((ans, ind) => {
              if (index === ind) {
                return value;
              }
              return props.exercises.correctAnswer[ind];
            });
            props.setCorrectAnswer(newAnswer);
          }
        };
        const isTrueSelected =
          (props.exercises && props.exercises.correctAnswer[i] === "true") ||
          (props.exercises && props.exercises.correctAnswer[i] === true);
        const isFalseSelected =
          (props.exercises && props.exercises.correctAnswer[i] === "false") ||
          (props.exercises && props.exercises.correctAnswer[i] === false);

        answers.push(
          <div key={i + 1} className={"row answerRow"}>
            <div className={"col-xs-10"}>
              <PluginPlaceholder
                {...props}
                key={i + 1}
                pluginContainerName={i18n.t("TrueFalse.Answer") + " " + (i + 1)}
                pluginDefaultContent={[
                  {
                    plugin: "BasicText",
                    initialState: {
                      __i18nKey: "TrueFalse.Answer",
                      __suffix: " " + (1 + i),
                      __text: "<p>" + contentT("TrueFalse.Answer") + " " + (1 + i) + "</p>",
                    },
                  },
                ]}
                pluginContainer={"Answer" + i}
              />
            </div>
            <div className="tf-options">
              <div
                className={`tf-option ${isTrueSelected ? "selected" : ""}`}
                style={getTrueFalseOptionVars(state, "true")}
                onClick={() => clickHandler(i, "true")}
              >
                <span className="tf-option-icon">✓</span>
                <span className="tf-option-label">
                  {state.trueText || contentT("TrueFalse.True") || "True"}
                </span>
              </div>
              <div
                className={`tf-option ${isFalseSelected ? "selected" : ""}`}
                style={getTrueFalseOptionVars(state, "false")}
                onClick={() => clickHandler(i, "false")}
              >
                <span className="tf-option-icon">✕</span>
                <span className="tf-option-label">
                  {state.falseText || contentT("TrueFalse.False") || "False"}
                </span>
              </div>
            </div>
            {props.onRemoveAnswer ? (
              <button
                type="button"
                className="assessment-remove-answer"
                aria-label={i18n.t("messages.remove_answer")}
                title={i18n.t("messages.remove_answer")}
                onClick={(e) => {
                  e.stopPropagation();
                  props.onRemoveAnswer(i);
                }}
              >
                ×
              </button>
            ) : null}
          </div>
        );
      }
      return (
        <div
          className={"exercisePlugin truefalsePlugin"}
          style={{ "--q-btn-hover-bg": state.buttonHoverBackgroundColor || "#ede9fe" }}
        >
          <div className={"row"} key={-1}>
            <div className={"col-xs-12"}>
              <PluginPlaceholder
                {...props}
                key="1"
                pluginContainerName={i18n.t("TrueFalse.Question")}
                pluginDefaultContent={[
                  {
                    plugin: "BasicText",
                    initialState: {
                      __i18nKey: "TrueFalse.Statement",
                      __text: "<p>" + contentT("TrueFalse.Statement") + "</p>",
                    },
                  },
                ]}
                pluginContainer={"Question"}
              />
            </div>
          </div>
          <div className="q-answer-list">{answers}</div>
          {props.onAddAnswer ? (
            <button
              type="button"
              className="assessment-add-answer"
              onClick={(e) => {
                e.stopPropagation();
                props.onAddAnswer();
              }}
            >
              + {i18n.t("messages.add_answer")}
            </button>
          ) : null}
          <div
            className={"row feedbackRow"}
            key={-2}
            style={{ display: state.showFeedback ? "block" : "none" }}
          >
            <div
              className={"col-xs-12 feedback"}
              style={{
                color: /* quizColor */ "black",
                borderColor: "#318533",
                backgroundColor: setRgbaAlpha("rgba(39,208,124,0.20)", 0.15),
              }}
            >
              <PluginPlaceholder
                {...props}
                key="-2"
                pluginContainerName={i18n.t("MultipleChoice.Feedback")}
                pluginContainer={"TrueFeedback"}
                pluginDefaultContent={[
                  {
                    plugin: "BasicText",
                    initialState: {
                      __i18nKey: "MultipleChoice.TrueFeedbackMsg",
                      __text: "<p>" + contentT("MultipleChoice.TrueFeedbackMsg") + "</p>",
                    },
                  },
                ]}
              />
            </div>
          </div>
          <div
            className={"row feedbackRow"}
            key={-3}
            style={{ display: state.showFeedback ? "block" : "none" }}
          >
            <div
              className={"col-xs-12 feedback"}
              style={{
                color: /* quizColor */ "black",
                borderColor: "#a31111",
                backgroundColor: setRgbaAlpha("rgba(213,26,26,0.20)", 0.15),
              }}
            >
              <PluginPlaceholder
                {...props}
                key="-3"
                pluginContainerName={i18n.t("MultipleChoice.Feedback")}
                pluginContainer={"FalseFeedback"}
                pluginDefaultContent={[
                  {
                    plugin: "BasicText",
                    initialState: {
                      __i18nKey: "MultipleChoice.FalseFeedbackMsg",
                      __text: "<p>" + contentT("MultipleChoice.FalseFeedbackMsg") + "</p>",
                    },
                  },
                ]}
              />
            </div>
          </div>
          <style
            dangerouslySetInnerHTML={{
              __html: `
                    .truefalsePlugin input[type="radio"]  {
                      background-color: transparent;
                    }
                   .truefalsePlugin input[type="radio"]:checked:after {
                      background-color: rgb(49 161 72);
                    }
                  `,
            }}
          />
        </div>
      );
    },
  };
}
/* eslint-enable react/prop-types */
